import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";

type Entry = {
  id: number;
  createdAt: string;
  actorId: number | null;
  actorEmail: string | null;
  actorRole: string | null;
  action: string;
  targetType: string | null;
  targetId: number | null;
  outcome: "success" | "denied" | "failure";
  detail: string | null;
  ip: string | null;
  userAgent: string | null;
};

const OUTCOME_STYLES: Record<string, string> = {
  success: "bg-teal-100 text-teal-700",
  denied: "bg-red-100 text-red-700",
  failure: "bg-amber-100 text-amber-700",
};

const PAGE_SIZES = [10, 20, 50] as const;
const RANGES = ["", "day", "week", "month", "year"] as const;

/** Windowed page numbers around the current page, with the first/last
 * always visible and `...` gaps -- avoids rendering hundreds of buttons
 * once the log has grown to many pages. */
function pageWindow(current: number, total: number): (number | "...")[] {
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | "...")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("...");
    out.push(sorted[i]);
  }
  return out;
}

const AuditLog: React.FC = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [range, setRange] = useState<(typeof RANGES)[number]>("");
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(20);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deniedLastDay, setDeniedLastDay] = useState(0);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (outcome) params.set("outcome", outcome);
      if (actionFilter.trim()) params.set("action", actionFilter.trim());
      if (range) params.set("range", range);
      params.set("pageSize", String(pageSize));
      params.set("page", String(targetPage));
      const res = await apiFetch(`/api/superadmin/audit-log?${params.toString()}`);
      if (!res || !res.ok) {
        setError(t("auditLog.failedToLoad"));
        return;
      }
      const data = await res.json();
      setEntries(data.entries);
      setPage(data.page);
      setTotalPages(data.totalPages);
      setTotal(data.total);
      setDeniedLastDay(data.deniedLastDay);
    } catch {
      setError(t("auditLog.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [outcome, actionFilter, range, pageSize, t]);

  // Any filter/range/page-size change restarts at page 1 -- the old page
  // number is meaningless against a different result set.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome, range, pageSize]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">{t("auditLog.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("auditLog.subtitle")}</p>
        </div>
        {/* Denied entries are the ones worth noticing: that's where blocked
            cross-tenant attempts land. */}
        {deniedLastDay > 0 && (
          <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {t("auditLog.deniedLastDay", { count: deniedLastDay })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          className="border rounded p-2 text-sm"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
        >
          <option value="">{t("auditLog.allOutcomes")}</option>
          <option value="denied">{t("auditLog.outcomeDenied")}</option>
          <option value="success">{t("auditLog.outcomeSuccess")}</option>
          <option value="failure">{t("auditLog.outcomeFailure")}</option>
        </select>
        <select
          className="border rounded p-2 text-sm"
          value={range}
          onChange={(e) => setRange(e.target.value as (typeof RANGES)[number])}
        >
          <option value="">{t("auditLog.range.all")}</option>
          <option value="day">{t("auditLog.range.day")}</option>
          <option value="week">{t("auditLog.range.week")}</option>
          <option value="month">{t("auditLog.range.month")}</option>
          <option value="year">{t("auditLog.range.year")}</option>
        </select>
        <input
          className="border rounded p-2 text-sm flex-1 min-w-[12rem]"
          type="text"
          placeholder={t("auditLog.actionFilter")}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
        />
        <button
          onClick={() => load(1)}
          className="px-4 py-2 bg-teal-600 text-white rounded font-semibold text-sm hover:bg-teal-700 transition"
        >
          {t("auditLog.apply")}
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-600 ml-auto">
          {t("auditLog.perPage")}
          <select
            className="border rounded p-2 text-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value) as (typeof PAGE_SIZES)[number])}
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {loading && entries.length === 0 ? (
        <div className="text-gray-500">{t("common.loading")}</div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          {t("auditLog.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.when")}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.action")}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.actor")}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.target")}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.ip")}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t("auditLog.detail")}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600 tabular-nums">
                      {new Date(e.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${OUTCOME_STYLES[e.outcome] || "bg-gray-100 text-gray-600"}`}>
                        {t(`auditLog.outcome.${e.outcome}`, { defaultValue: e.outcome })}
                      </span>
                      <span className="ml-2 font-mono text-xs">{e.action}</span>
                    </td>
                    <td className="px-3 py-2">
                      {e.actorEmail || t("auditLog.anonymous")}
                      {e.actorRole && <span className="text-xs text-gray-400 ml-1">({t(`roles.${e.actorRole}`, { defaultValue: e.actorRole })})</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {e.targetType ? `${e.targetType} #${e.targetId ?? "?"}` : "-"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">{e.ip || "-"}</td>
                    <td className="px-3 py-2 text-gray-600">{e.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="text-xs text-gray-500">
              {t("auditLog.totalCount", { count: total })}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => load(page - 1)}
                disabled={loading || page <= 1}
                className="px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t("auditLog.prev")}
              </button>
              {pageWindow(page, totalPages).map((p, i) =>
                p === "..." ? (
                  <span key={`gap-${i}`} className="px-2 text-sm text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => load(p)}
                    disabled={loading}
                    className={`px-3 py-1.5 border rounded text-sm ${p === page ? "bg-teal-600 text-white border-teal-600" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => load(page + 1)}
                disabled={loading || page >= totalPages}
                className="px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t("auditLog.next")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AuditLog;
